import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PasswordValidationResult } from '../utils/passwordValidator';

interface PasswordRequirementsProps {
  validation: PasswordValidationResult;
  showAll?: boolean;
}

export const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({
  validation,
  showAll = false,
}) => {
  const { t } = useTranslation();

  const requirements = [
    {
      key: 'minLength',
      check: validation.checks.minLength,
      label: t('auth.passwordRequirementMinLength'),
    },
    {
      key: 'hasUpperCase',
      check: validation.checks.hasUpperCase,
      label: t('auth.passwordRequirementUpperCase'),
    },
    {
      key: 'hasLowerCase',
      check: validation.checks.hasLowerCase,
      label: t('auth.passwordRequirementLowerCase'),
    },
    {
      key: 'hasNumber',
      check: validation.checks.hasNumber,
      label: t('auth.passwordRequirementNumber'),
    },
    {
      key: 'hasSpecialChar',
      check: validation.checks.hasSpecialChar,
      label: t('auth.passwordRequirementSpecial'),
    },
  ];

  // Mostrar solo los requisitos básicos por defecto, o todos si showAll es true
  const visibleRequirements = showAll
    ? [
        ...requirements,
        {
          key: 'notCommon',
          check: validation.checks.notCommon,
          label: t('auth.passwordRequirementNotCommon'),
        },
        {
          key: 'noRepeatedChars',
          check: validation.checks.noRepeatedChars,
          label: t('auth.passwordRequirementNoRepeated'),
        },
        {
          key: 'noSequences',
          check: validation.checks.noSequences,
          label: t('auth.passwordRequirementNoSequence'),
        },
      ]
    : requirements;

  return (
    <Box sx={{ mt: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.75, display: 'block' }}>
        {t('auth.passwordRequirements')}
      </Typography>
      <Grid container spacing={1}>
        {visibleRequirements.map((req) => (
          <Grid item xs={6} key={req.key}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {req.check ? (
                <CheckCircle color="success" sx={{ fontSize: 12.8 }} />
              ) : (
                <Cancel color="error" sx={{ fontSize: 12.8 }} />
              )}
              <Typography
                variant="caption"
                sx={{
                  color: req.check ? 'success.main' : 'text.secondary',
                  fontSize: '0.56rem',
                  lineHeight: 1.2,
                }}
              >
                {req.label}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

